// GPS Calculations Module - Pure GPS and distance calculations
// No caching, no database queries - just mathematical functions

// Pure GPS calculation functions extracted from telemetryService
export function calculateDistanceFromGPS(
  gpsPoints: { latitude: number; longitude: number }[]
): number {
  if (gpsPoints.length < 2) {
    return 0;
  }

  let totalDistance = 0;
  for (let i = 1; i < gpsPoints.length; i++) {
    const distance = calculateDistanceBetweenPoints(gpsPoints[i - 1], gpsPoints[i]);
    totalDistance += distance;
  }

  return Math.round(totalDistance); // Return rounded meters
}

export function calculateDistanceBetweenPoints(
  point1: { latitude: number; longitude: number },
  point2: { latitude: number; longitude: number }
): number {
  const R = 6371000; // Earth's radius in meters
  const lat1Rad = (point1.latitude * Math.PI) / 180;
  const lat2Rad = (point2.latitude * Math.PI) / 180;
  const deltaLatRad = ((point2.latitude - point1.latitude) * Math.PI) / 180;
  const deltaLonRad = ((point2.longitude - point1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLonRad / 2) * Math.sin(deltaLonRad / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Calculate speed between two GPS points
 * @param point1 - First GPS point with latitude, longitude, and timestamp
 * @param point2 - Second GPS point with latitude, longitude, and timestamp
 * @returns Speed in meters per second
 */
export function calculateSpeedBetweenPoints(
  point1: { latitude: number; longitude: number; timestamp: number },
  point2: { latitude: number; longitude: number; timestamp: number }
): number {
  const distance = calculateDistanceBetweenPoints(point1, point2);
  const timeDiff = (point2.timestamp - point1.timestamp) / 1000; // Convert to seconds

  if (timeDiff <= 0) {
    return 0; // Avoid division by zero or negative time
  }

  return distance / timeDiff; // Speed in m/s
}

/**
 * Calculate speeds for an array of GPS points
 * @param gpsPoints - Array of GPS points with latitude, longitude, and timestamp
 * @returns Array of GPS points with calculated speed values
 */
export function calculateSpeedsFromGPS(
  gpsPoints: { latitude: number; longitude: number; timestamp: number }[]
): Array<{ latitude: number; longitude: number; timestamp: number; speed: number }> {
  if (gpsPoints.length < 2) {
    return gpsPoints.map((point) => ({ ...point, speed: 0 }));
  }

  return gpsPoints.map((point, index) => {
    let speed = 0;

    if (index > 0) {
      // Calculate speed from previous point
      speed = calculateSpeedBetweenPoints(gpsPoints[index - 1], point);
    }

    return {
      ...point,
      speed: Math.max(0, speed), // Ensure speed is never negative
    };
  });
}

export function processSpeedProfile(
  gpsData: any[]
): Array<{ timestamp: number; speed: number; distance: number; accel: number }> {
  // Calculate acceleration from speed changes
  const dataWithAccel = (gpsData || []).map((point: any, index: number, array: any[]) => {
    let accel = 0;

    if (index > 0 && index < array.length) {
      const prevPoint = array[index - 1];
      const timeDiff = (point.timestamp - prevPoint.timestamp) / 1000; // Convert to seconds
      const speedDiff = point.speed - prevPoint.speed;
      accel = timeDiff > 0 ? speedDiff / timeDiff : 0;
    }

    return {
      timestamp: point.timestamp as number,
      speed: point.speed as number,
      distance: point.distance as number,
      accel: Math.round(accel * 100) / 100, // Round to 2 decimal places
    };
  });

  return dataWithAccel;
}
